/**
 * IncidentReports.jsx
 * --------------------------------------------------
 * Incident Report Management (AFH / DSHS / RN)
 *
 * ✔ Incident history (list)
 * ✔ Create new incident (all roles)
 * ✔ Submit = locked + awaiting review
 * ✔ Review = RN/Admin only
 * ✔ Immutable content
 * ✔ Print-safe
 */

import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
    Box,
    Grid,
    TextField,
    Typography,
    Button,
    Paper,
    Divider,
    Stack,
    List,
    ListItemButton,
    ListItemText,
    Chip,
    MenuItem,
    Tooltip
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";

import { useReactToPrint } from "react-to-print";
import { gql, useMutation, useQuery } from "@apollo/client";
import DocumentSignatureSection from "../common/documentSignatureSection";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../platform/redux/store";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../../platform/graphql/queries";
import UpgradePlan from "../../../../platform/util/upgrade/upgradePlan";
import { getEffectivePlan, getTrialDaysLeft } from "../../../../platform/util/utils";

/* =====================================================
   MOCK USER ROLE (REPLACE WITH AUTH CONTEXT LATER)
   ===================================================== */
const userRole = "RN"; // "STAFF" | "RN" | "ADMIN"

/* =====================================================
   CONSTANTS
   ===================================================== */
const EMPTY_REPORT = {
    incidentDate: "",
    incidentTime: "",
    incidentType: "",
    incidentTypeOther: "",
    location: "",
    description: "",
    injuryDescription: "",
    immediateAction: "",
    notifications: "",
    witnesses: "",
    reportedBy: ""
};

const FORM_FIELDS = [
    "incidentDate",
    "incidentTime",
    "incidentType",
    "location",
    "description",
    "injuryDescription",
    "immediateAction",
    "notifications",
    "witnesses",
    "reportedBy"
];

const INCIDENT_TYPE_OPTIONS = [
    "Fall",
    "Medication Error",
    "Injury",
    "Behavioral",
    "Wandering/Elopement",
    "Abuse/Neglect",
    "Property Damage",
    "Medical Emergency",
    "Other"
];

const DATE_RANGE_YEARS = (() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 10; y -= 1) {
        years.push(String(y));
    }
    return years;
})();

const MONTH_OPTIONS = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
];

const TIME_OPTIONS = (() => {
    const options = [];
    for (let hour = 0; hour < 24; hour += 1) {
        for (let minute = 0; minute < 60; minute += 15) {
            const hour12 = ((hour + 11) % 12) + 1;
            const period = hour < 12 ? "AM" : "PM";
            const label = `${String(hour12).padStart(2, "0")}:${String(
                minute
            ).padStart(2, "0")} ${period}`;
            options.push(label);
        }
    }
    return options;
})();

const getDateParts = (value) => {
    if (!value || typeof value !== "string") {
        return { year: "", month: "", day: "" };
    }
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) {
        return { year: "", month: "", day: "" };
    }
    return { year, month, day };
};

const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(Number(year), Number(month), 0).getDate();
};

const composeDate = (parts) => {
    const { year, month, day } = parts;
    if (!year || !month || !day) return "";
    return `${year}-${month}-${day}`;
};

const BODY_DIAGRAMS = [
    {
        title: "Front View",
        direction: "front",
        points: [
            { name: "Head", x: 50, y: 11 },
            { name: "Face", x: 50, y: 17 },
            { name: "Neck", x: 50, y: 23 },
            { name: "Chest", x: 50, y: 31 },
            { name: "Abdomen", x: 50, y: 39 },
            { name: "Left Shoulder", x: 34, y: 28 },
            { name: "Right Shoulder", x: 66, y: 28 },
            { name: "Left Arm", x: 26, y: 39 },
            { name: "Right Arm", x: 74, y: 39 },
            { name: "Left Hand", x: 22, y: 56 },
            { name: "Right Hand", x: 78, y: 56 },
            { name: "Pelvis", x: 50, y: 49 },
            { name: "Left Thigh", x: 44, y: 61 },
            { name: "Right Thigh", x: 56, y: 61 },
            { name: "Left Knee", x: 44, y: 72 },
            { name: "Right Knee", x: 56, y: 72 },
            { name: "Left Lower Leg", x: 44, y: 82 },
            { name: "Right Lower Leg", x: 56, y: 82 },
            { name: "Left Foot", x: 42, y: 93 },
            { name: "Right Foot", x: 58, y: 93 },
        ],
    },
    {
        title: "Back View",
        direction: "back",
        points: [
            { name: "Back of Head", x: 50, y: 14 },
            { name: "Upper Back", x: 50, y: 29 },
            { name: "Mid Back", x: 50, y: 37 },
            { name: "Lower Back", x: 50, y: 44 },
            { name: "Left Scapula", x: 41, y: 30 },
            { name: "Right Scapula", x: 59, y: 30 },
            { name: "Left Elbow", x: 27, y: 46 },
            { name: "Right Elbow", x: 73, y: 46 },
            { name: "Left Forearm", x: 24, y: 54 },
            { name: "Right Forearm", x: 76, y: 54 },
            { name: "Left Wrist", x: 22, y: 60 },
            { name: "Right Wrist", x: 78, y: 60 },
            { name: "Buttock", x: 50, y: 52 },
            { name: "Left Hamstring", x: 44, y: 63 },
            { name: "Right Hamstring", x: 56, y: 63 },
            { name: "Left Calf", x: 44, y: 81 },
            { name: "Right Calf", x: 56, y: 81 },
            { name: "Left Heel", x: 42, y: 94 },
            { name: "Right Heel", x: 58, y: 94 },
        ],
    },
];

const BodySilhouette = ({ direction = "front" }) => (
    <svg
        viewBox="0 0 220 420"
        width="100%"
        height="100%"
        aria-hidden="true"
        style={{ display: "block" }}
    >
        <g fill={direction === "front" ? "#dbeafe" : "#e2e8f0"} stroke="#94a3b8" strokeWidth="2">
            <circle cx="110" cy="48" r="22" />
            <rect x="98" y="70" width="24" height="24" rx="10" />
            <rect x="76" y="94" width="68" height="78" rx="24" />
            <rect x="52" y="102" width="20" height="92" rx="10" />
            <rect x="148" y="102" width="20" height="92" rx="10" />
            <rect x="88" y="172" width="44" height="40" rx="14" />
            <rect x="88" y="212" width="18" height="130" rx="10" />
            <rect x="114" y="212" width="18" height="130" rx="10" />
            <ellipse cx="97" cy="354" rx="18" ry="10" />
            <ellipse cx="123" cy="354" rx="18" ry="10" />
        </g>
    </svg>
);

const getBodyPartsFromDescription = (value) => {
    const lines = String(value || "").split(/\r?\n/);
    const bodyLine = lines.find((line) => /^Body part\(s\):/i.test(line.trim()));
    if (!bodyLine) return [];
    const raw = bodyLine.replace(/^Body part\(s\):/i, "");
    return raw
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

const removeBodyPartsLine = (value) =>
    String(value || "")
        .split(/\r?\n/)
        .filter((line) => !/^Body part\(s\):/i.test(line.trim()))
        .join("\n")
        .trim();

const applyBodyPartsToDescription = (value, bodyParts = []) => {
    const cleaned = removeBodyPartsLine(value);
    if (!bodyParts.length) return cleaned;
    const prefix = `Body part(s): ${bodyParts.join(", ")}`;
    return cleaned ? `${prefix}\n${cleaned}` : prefix;
};

const FIELD_LABELS = {
    incidentDate: "Incident Date",
    incidentTime: "Incident Time",
    incidentType: "Incident Type",
    location: "Location",
    description: "Description",
    injuryDescription: "Injury Description",
    immediateAction: "Immediate Action",
    notifications: "Notifications",
    witnesses: "Witnesses",
    reportedBy: "Reported By",
    incidentTypeOther: "Other Incident Type",
};

const getReporterName = (report, fallback = "") => {
    const explicit = String(report?.reportedBy || "").trim();
    if (explicit) return explicit;
    const staffUser = String(report?.staff?.user?.username || "").trim();
    if (staffUser) return staffUser;
    return fallback;
};

const formatAuditTimestamp = (value) => {
    if (!value) return "Not recorded";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString();
};

/* =====================================================
   GRAPHQL
   ===================================================== */
const GET_INCIDENT_REPORTS = gql`
  query GetIncidentReports($residentId: ID!) {
    getIncidentReports(residentId: $residentId) {
      id
      residentId
      staffId
      incidentDate
      incidentTime
      incidentType
      location
      description
      injuryDescription
      immediateAction
      notifications
      witnesses
      reportedBy
      status
      reviewedByStaffId
      reviewedAt
      createdAt
      updatedAt
      staff {
        id
        user {
          id
          username
        }
      }
      reviewedBy {
        id
        user {
          id
          username
        }
      }
    }
  }
`;

const ADD_INCIDENT_REPORT = gql`
  mutation AddIncidentReport($data: IncidentReportInput!) {
    addIncidentReport(data: $data) {
      id
      residentId
      staffId
      incidentDate
      incidentTime
      incidentType
      location
      description
      injuryDescription
      immediateAction
      notifications
      witnesses
      reportedBy
      status
      reviewedByStaffId
      reviewedAt
      createdAt
      updatedAt
      staff {
        id
        user {
          id
          username
        }
      }
    }
  }
`;

const UPDATE_INCIDENT_REPORT_STATUS = gql`
  mutation UpdateIncidentReportStatus($id: ID!, $status: String!, $reviewedByStaffId: ID) {
    updateIncidentReportStatus(id: $id, status: $status, reviewedByStaffId: $reviewedByStaffId) {
      id
      residentId
      staffId
      incidentDate
      incidentTime
      incidentType
      location
      description
      injuryDescription
      immediateAction
      notifications
      witnesses
      reportedBy
      status
      reviewedByStaffId
      reviewedAt
      createdAt
      updatedAt
      staff {
        id
        user {
          id
          username
        }
      }
      reviewedBy {
        id
        user {
          id
          username
        }
      }
    }
  }
`;

/* =====================================================
   COMPONENT
   ===================================================== */
export default function IncidentReports({ resident }) {
    const { providerUser } = useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const businessId = selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId;
    const printRef = useRef();

    // view modes
    const [mode, setMode] = useState("list"); // list | create | view

    // data
    const [activeReport, setActiveReport] = useState(null);
    const [draft, setDraft] = useState(EMPTY_REPORT);
    const [reportSignatures, setReportSignatures] = useState({});
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const residentId = resident?.id || null;
    const activeUsername = String(providerUser?.username || "").trim();
    const selectedBodyParts = useMemo(
        () => getBodyPartsFromDescription(draft?.injuryDescription),
        [draft?.injuryDescription]
    );
    const requiredCreateFields = useMemo(
        () => [
            "incidentDate",
            "incidentTime",
            "incidentType",
            "location",
            "description",
            "injuryDescription",
            "immediateAction",
            "notifications",
            "witnesses",
            "reportedBy",
        ],
        []
    );
    const missingCreateFields = useMemo(() => {
        if (mode !== "create") return [];
        const missing = requiredCreateFields.filter(
            (field) => !String(draft?.[field] || "").trim()
        );
        if (
            String(draft?.incidentType || "").trim() === "Other" &&
            !String(draft?.incidentTypeOther || "").trim()
        ) {
            missing.push("incidentTypeOther");
        }
        return missing;
    }, [draft, mode, requiredCreateFields]);
    const missingCreateFieldSet = useMemo(
        () => new Set(missingCreateFields),
        [missingCreateFields]
    );
    const toggleBodyPart = (partName) => {
        setDraft((prev) => {
            const currentParts = getBodyPartsFromDescription(prev?.injuryDescription);
            const exists = currentParts.some(
                (part) => part.toLowerCase() === String(partName).toLowerCase()
            );
            const nextParts = exists
                ? currentParts.filter(
                      (part) => part.toLowerCase() !== String(partName).toLowerCase()
                  )
                : [...currentParts, partName];
            return {
                ...prev,
                injuryDescription: applyBodyPartsToDescription(
                    prev?.injuryDescription,
                    nextParts
                ),
            };
        });
    };
    const incidentSignatureRoles = useMemo(
        () => [
            {
                key: "reportedBy",
                label: "Reported By Signature",
                helper: "Person who documented this incident"
            },
            {
                key: "witness",
                label: "Witness Signature (If Applicable)",
                helper: "Optional witness attestation"
            },
            {
                key: "rnAdminReview",
                label: "RN / Admin Review Signature",
                helper: "Required for supervisory review"
            },
            {
                key: "residentPoaNotified",
                label: "Resident / POA Notified Signature",
                helper: "Acknowledgment of notification"
            }
        ],
        []
    );
    const incidentSignatureStorageKey = useMemo(
        () =>
            activeReport?.id
                ? `incidentReportSignatures:${residentId}:${activeReport.id}`
                : "",
        [residentId, activeReport?.id]
    );

    const {
        data,
        loading,
        error
    } = useQuery(GET_INCIDENT_REPORTS, {
        variables: { residentId },
        skip: !residentId,
        fetchPolicy: "cache-and-network"
    });

    const [addIncidentReport, { loading: saving }] = useMutation(ADD_INCIDENT_REPORT);
    const [updateIncidentReportStatus, { loading: updatingStatus }] = useMutation(
        UPDATE_INCIDENT_REPORT_STATUS
    );

    const reports = data?.getIncidentReports || [];
    const { data: billingData, loading: billingLoading } = useQuery(GET_BILLING_SUBSCRIPTION, {
        variables: { businessId },
        skip: !businessId,
        fetchPolicy: "network-only",
    });
    const { data: plansData, loading: plansLoading } = useQuery(GET_PLANS, {
        skip: !businessId,
    });
    const subscription = billingData?.getBillingSubscription;
    const premiumPlanId = Number(
        plansData?.plans?.find((plan) => plan.code === "PREMIUM")?.id || 3
    );
    const tokenWithSubscriptionPlan = {
        ...(providerUser || {}),
        planId: subscription?.planId ?? providerUser?.planId,
    };
    const effectivePlanLevel = Number(getEffectivePlan(tokenWithSubscriptionPlan) || 0);
    const trialDaysLeft = getTrialDaysLeft(providerUser || {});
    const hasActiveTrial =
        trialDaysLeft === "Unlimited" ||
        Number(trialDaysLeft) > 0 ||
        String(subscription?.status || "").toLowerCase() === "trialing";
    const hasSignatureAccess = Boolean(
        businessId && (effectivePlanLevel >= premiumPlanId || hasActiveTrial)
    );
    const hasPremiumComplianceAccess = hasSignatureAccess;
    const checkingPremium = Boolean(businessId) && (billingLoading || plansLoading);
    useEffect(() => {
        if (!incidentSignatureStorageKey) {
            setReportSignatures({});
            return;
        }
        try {
            const saved = localStorage.getItem(incidentSignatureStorageKey);
            setReportSignatures(saved ? JSON.parse(saved) : {});
        } catch (_err) {
            setReportSignatures({});
        }
    }, [incidentSignatureStorageKey]);
    const handleIncidentSignatureChange = (nextSignatures) => {
        setReportSignatures(nextSignatures || {});
        if (!incidentSignatureStorageKey) return;
        try {
            if (nextSignatures && Object.keys(nextSignatures).length > 0) {
                localStorage.setItem(
                    incidentSignatureStorageKey,
                    JSON.stringify(nextSignatures)
                );
            } else {
                localStorage.removeItem(incidentSignatureStorageKey);
            }
        } catch (_err) {
            // Non-blocking for in-session preview.
        }
    };

    /* -----------------------------------------------------
       PRINT
       ----------------------------------------------------- */
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Incident_Report_${resident?.lastName || ""}`
    });

    /* -----------------------------------------------------
       PERMISSIONS
       ----------------------------------------------------- */
    const canReview = userRole === "RN" || userRole === "ADMIN";
    const residentDisplayName =
        [resident?.firstName, resident?.lastName].filter(Boolean).join(" ").trim() ||
        `Resident #${resident?.id || ""}`;
    const printedAt = new Date().toLocaleString();

    /* -----------------------------------------------------
       STATUS UPDATE (RN / ADMIN)
       ----------------------------------------------------- */
    const updateStatus = async (id, newStatus) => {
        const result = await updateIncidentReportStatus({
            variables: { id, status: newStatus },
            update: (cache, { data: m }) => {
                const updated = m?.updateIncidentReportStatus;
                if (!updated || !residentId) return;

                const existing = cache.readQuery({
                    query: GET_INCIDENT_REPORTS,
                    variables: { residentId }
                });

                if (!existing?.getIncidentReports) return;

                cache.writeQuery({
                    query: GET_INCIDENT_REPORTS,
                    variables: { residentId },
                    data: {
                        getIncidentReports: existing.getIncidentReports.map((r) =>
                            r.id === updated.id ? updated : r
                        )
                    }
                });
            }
        });

        if (result?.data?.updateIncidentReportStatus) {
            setActiveReport(result.data.updateIncidentReportStatus);
        }
    };

    /* =====================================================
       RENDER
       ===================================================== */

    if (!resident) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">
                    Loading incident reports…
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">
                    Loading incident reports…
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">
                    Failed to load incident reports.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* ================= HEADER ================= */}
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                    Incident Reports
                </Typography>

                <Stack direction="row" spacing={1} className="no-print">
                    {mode === "list" && (
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={() => {
                                setDraft({
                                    ...EMPTY_REPORT,
                                    reportedBy: activeUsername,
                                });
                                setShowValidation(false);
                                setMode("create");
                            }}
                        >
                            Create Incident
                        </Button>
                    )}

                    {(mode === "view" || mode === "create") && (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            variant="outlined"
                            onClick={() => setMode("list")}
                        >
                            Back to List
                        </Button>
                    )}

                    {mode === "view" && (
                        <Button
                            startIcon={<PrintIcon />}
                            variant="outlined"
                            onClick={handlePrint}
                        >
                            Print
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* ================= LIST VIEW ================= */}
            {mode === "list" && (
                <Paper sx={{ p: 2 }}>
                    {reports.length === 0 && (
                        <Typography color="text.secondary">
                            No incident reports recorded.
                        </Typography>
                    )}

                    <List>
                        {reports.map((r) => (
                            <ListItemButton
                                key={r.id}
                                onClick={() => {
                                    setActiveReport(r);
                                    setMode("view");
                                }}
                            >
                                <ListItemText
                                    primary={`${r.incidentDate} – ${r.incidentType}`}
                                    secondary={`Reported by ${getReporterName(r) || "Unknown"}`}
                                />
                                <Chip
                                    label={r.status}
                                    size="small"
                                    color={
                                        r.status === "Reviewed"
                                            ? "success"
                                            : "warning"
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}

            {/* ================= CREATE / VIEW ================= */}
            {(mode === "create" || mode === "view") && (
                <Paper ref={printRef} sx={{ p: 3 }}>
                    <Box
                        className="print-fixed-header"
                        sx={{
                            display: "none",
                            "@media print": {
                                display: "block",
                            }
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Form: Incident Report | Resident: {residentDisplayName}
                            </Typography>
                            <Typography variant="caption">Printed: {printedAt}</Typography>
                        </Stack>
                    </Box>

                    <Typography
                        fontWeight={600}
                        sx={{ mb: 2, "@media print": { display: "none" } }}
                    >
                        Resident: {resident.firstName} {resident.lastName}
                    </Typography>
                    <Box className="print-content-offset">
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={2}>
                        {FORM_FIELDS.map((field) => {
                            if (field === "incidentDate" && mode === "create") {
                                const dateParts = getDateParts(draft.incidentDate);
                                const fallbackYear = String(new Date().getFullYear());
                                const fallbackMonth = String(
                                    new Date().getMonth() + 1
                                ).padStart(2, "0");
                                const dayLimit = getDaysInMonth(
                                    dateParts.year,
                                    dateParts.month
                                );

                                return (
                                    <Grid item xs={12} key={field}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mb: 0.5 }}
                                        >
                                            Incident Date
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                label="Month"
                                                select
                                                size="small"
                                                fullWidth
                                                value={dateParts.month}
                                                onChange={(e) => {
                                                    const nextMonth = e.target.value;
                                                    const nextYear =
                                                        dateParts.year || fallbackYear;
                                                    const nextDay = Math.min(
                                                        Number(dateParts.day || 1),
                                                        getDaysInMonth(
                                                            nextYear,
                                                            nextMonth
                                                        )
                                                    );
                                                    const nextParts = {
                                                        ...dateParts,
                                                        year: nextYear,
                                                        month: nextMonth,
                                                        day: String(nextDay).padStart(2, "0")
                                                    };
                                                    setDraft({
                                                        ...draft,
                                                        incidentDate: composeDate(nextParts)
                                                    });
                                                }}
                                            >
                                                {MONTH_OPTIONS.map((month) => (
                                                    <MenuItem
                                                        key={month.value}
                                                        value={month.value}
                                                    >
                                                        {month.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>

                                            <TextField
                                                label="Day"
                                                select
                                                size="small"
                                                fullWidth
                                                value={dateParts.day}
                                                onChange={(e) => {
                                                    const nextYear =
                                                        dateParts.year || fallbackYear;
                                                    const nextMonth =
                                                        dateParts.month || fallbackMonth;
                                                    const nextParts = {
                                                        ...dateParts,
                                                        year: nextYear,
                                                        month: nextMonth,
                                                        day: e.target.value
                                                    };
                                                    setDraft({
                                                        ...draft,
                                                        incidentDate: composeDate(nextParts)
                                                    });
                                                }}
                                            >
                                                {Array.from(
                                                    { length: dayLimit },
                                                    (_, index) => {
                                                        const dayValue = String(
                                                            index + 1
                                                        ).padStart(2, "0");
                                                        return (
                                                            <MenuItem
                                                                key={dayValue}
                                                                value={dayValue}
                                                            >
                                                                {dayValue}
                                                            </MenuItem>
                                                        );
                                                    }
                                                )}
                                            </TextField>

                                            <TextField
                                                label="Year"
                                                select
                                                size="small"
                                                fullWidth
                                                value={dateParts.year}
                                                onChange={(e) => {
                                                    const nextYear = e.target.value;
                                                    const nextMonth =
                                                        dateParts.month || fallbackMonth;
                                                    const nextDay = Math.min(
                                                        Number(dateParts.day || 1),
                                                        getDaysInMonth(
                                                            nextYear,
                                                            nextMonth
                                                        )
                                                    );
                                                    const nextParts = {
                                                        ...dateParts,
                                                        year: nextYear,
                                                        month: nextMonth,
                                                        day: String(nextDay).padStart(2, "0")
                                                    };
                                                    setDraft({
                                                        ...draft,
                incidentDate: composeDate(nextParts)
            });
        }}
    >
        {DATE_RANGE_YEARS.map((year) => (
            <MenuItem key={year} value={year}>
                {year}
            </MenuItem>
        ))}
                                            </TextField>
                                        </Stack>
                                        {showValidation && missingCreateFieldSet.has("incidentDate") && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
                                                {FIELD_LABELS.incidentDate} is required.
                                            </Typography>
                                        )}
                                    </Grid>
                                );
                            }

                            if (field === "incidentTime" && mode === "create") {
                                return (
                                    <Grid item xs={12} key={field}>
                                        <TextField
                                            label="Incident Time"
                                            select
                                            fullWidth
                                            size="small"
                                            required
                                            error={showValidation && missingCreateFieldSet.has("incidentTime")}
                                            helperText={
                                                showValidation && missingCreateFieldSet.has("incidentTime")
                                                    ? `${FIELD_LABELS.incidentTime} is required.`
                                                    : ""
                                            }
                                            value={draft.incidentTime || ""}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    incidentTime: e.target.value
                                                })
                                            }
                                        >
                                            {TIME_OPTIONS.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                );
                            }

                            if (field === "incidentType" && mode === "create") {
                                const isOther = draft.incidentType === "Other";
                                return (
                                    <Grid item xs={12} key={field}>
                                        <TextField
                                            label="Incident Type"
                                            select
                                            fullWidth
                                            size="small"
                                            required
                                            error={showValidation && missingCreateFieldSet.has("incidentType")}
                                            helperText={
                                                showValidation && missingCreateFieldSet.has("incidentType")
                                                    ? `${FIELD_LABELS.incidentType} is required.`
                                                    : ""
                                            }
                                            value={draft.incidentType || ""}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    incidentType: e.target.value,
                                                    incidentTypeOther:
                                                        e.target.value === "Other"
                                                            ? draft.incidentTypeOther
                                                            : ""
                                                })
                                            }
                                        >
                                            {INCIDENT_TYPE_OPTIONS.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        {isOther && (
                                            <Box sx={{ mt: 2 }}>
                                                <TextField
                                                    label="Other Incident Type"
                                                    fullWidth
                                                    size="small"
                                                    required
                                                    error={showValidation && missingCreateFieldSet.has("incidentTypeOther")}
                                                    helperText={
                                                        showValidation && missingCreateFieldSet.has("incidentTypeOther")
                                                            ? `${FIELD_LABELS.incidentTypeOther} is required.`
                                                            : ""
                                                    }
                                                    value={draft.incidentTypeOther || ""}
                                                    onChange={(e) =>
                                                        setDraft({
                                                            ...draft,
                                                            incidentTypeOther: e.target.value
                                                        })
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                );
                            }

                            const value =
                                mode === "view"
                                    ? field === "reportedBy"
                                        ? getReporterName(activeReport)
                                        : activeReport?.[field] || ""
                                    : draft[field] || "";

                            return (
                                <Grid item xs={12} key={field}>
                                    <TextField
                                        label={field.replace(/([A-Z])/g, " $1")}
                                        fullWidth
                                        multiline
                                        size="small"
                                        required={mode === "create"}
                                        error={
                                            mode === "create" &&
                                            showValidation &&
                                            missingCreateFieldSet.has(field)
                                        }
                                        helperText={
                                            mode === "create" &&
                                            showValidation &&
                                            missingCreateFieldSet.has(field)
                                                ? `${FIELD_LABELS[field] || field} is required.`
                                                : ""
                                        }
                                        disabled={mode === "view"}
                                        value={value}
                                        onChange={(e) =>
                                            setDraft({
                                                ...draft,
                                                [field]: e.target.value
                                            })
                                        }
                                    />
                                    {mode === "create" && field === "injuryDescription" && (
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                p: 1.5,
                                                border: "1px solid #e2e8f0",
                                                borderRadius: 1,
                                                backgroundColor: "#f8fafc",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ display: "block", mb: 1, fontWeight: 700 }}
                                            >
                                                Body Part Picker (click to add/remove)
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: "block", mb: 1 }}
                                            >
                                                Selected body parts are automatically added to Injury Description.
                                            </Typography>
                                            <Grid container spacing={1.25}>
                                                {BODY_DIAGRAMS.map((group) => (
                                                    <Grid item xs={12} md={6} key={group.title}>
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                p: 1,
                                                                borderColor: "#dbe2ea",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ fontWeight: 700 }}
                                                            >
                                                                {group.title}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                    mx: "auto",
                                                                    width: 220,
                                                                    height: 360,
                                                                    position: "relative",
                                                                    border: "1px solid #e2e8f0",
                                                                    borderRadius: 1,
                                                                    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                                                                }}
                                                            >
                                                                <BodySilhouette direction={group.direction} />
                                                                {group.points.map((part) => {
                                                                    const selected = selectedBodyParts.some(
                                                                        (p) =>
                                                                            p.toLowerCase() ===
                                                                            part.name.toLowerCase()
                                                                    );
                                                                    return (
                                                                        <Tooltip title={part.name} key={`${group.title}-${part.name}`}>
                                                                            <Box
                                                                                component="button"
                                                                                type="button"
                                                                                onClick={() => toggleBodyPart(part.name)}
                                                                                sx={{
                                                                                    position: "absolute",
                                                                                    left: `${part.x}%`,
                                                                                    top: `${part.y}%`,
                                                                                    transform: "translate(-50%, -50%)",
                                                                                    width: 12,
                                                                                    height: 12,
                                                                                    borderRadius: "50%",
                                                                                    border: selected ? "2px solid #1d4ed8" : "1px solid #334155",
                                                                                    backgroundColor: selected ? "#3b82f6" : "#fff",
                                                                                    cursor: "pointer",
                                                                                    p: 0,
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    );
                                                                })}
                                                            </Box>
                                                            <Stack
                                                                direction="row"
                                                                spacing={0.75}
                                                                sx={{
                                                                    mt: 0.75,
                                                                    flexWrap: "wrap",
                                                                    gap: 0.75,
                                                                }}
                                                            >
                                                                {group.points.map((part) => {
                                                                    const selected = selectedBodyParts.some(
                                                                        (p) =>
                                                                            p.toLowerCase() ===
                                                                            part.name.toLowerCase()
                                                                    );
                                                                    return (
                                                                        <Chip
                                                                            key={`${group.title}-chip-${part.name}`}
                                                                            label={part.name}
                                                                            clickable
                                                                            size="small"
                                                                            color={selected ? "primary" : "default"}
                                                                            variant={selected ? "filled" : "outlined"}
                                                                            onClick={() => toggleBodyPart(part.name)}
                                                                        />
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    )}
                                </Grid>
                            );
                        })}
                        </Grid>

                    {/* ================= ACTIONS ================= */}
                        {mode === "create" && (
                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                disabled={saving || !residentId}
                                onClick={async () => {
                                    setShowValidation(true);
                                    if (missingCreateFields.length > 0) return;
                                    const normalizedIncidentType =
                                        draft.incidentType === "Other"
                                            ? (draft.incidentTypeOther || "").trim() ||
                                              "Other"
                                            : draft.incidentType;
                                    const normalizedReportedBy = getReporterName(
                                        draft,
                                        activeUsername
                                    );
                                    const { incidentTypeOther, ...draftPayload } = draft;
                                    const result = await addIncidentReport({
                                        variables: {
                                            data: {
                                                residentId,
                                                ...draftPayload,
                                                reportedBy: normalizedReportedBy,
                                                incidentType: normalizedIncidentType
                                            }
                                        },
                                        update: (cache, { data: m }) => {
                                            const created = m?.addIncidentReport;
                                            if (!created || !residentId) return;

                                            const existing = cache.readQuery({
                                                query: GET_INCIDENT_REPORTS,
                                                variables: { residentId }
                                            });

                                            cache.writeQuery({
                                                query: GET_INCIDENT_REPORTS,
                                                variables: { residentId },
                                                data: {
                                                    getIncidentReports: [
                                                        created,
                                                        ...(existing?.getIncidentReports || [])
                                                    ]
                                                }
                                            });
                                        }
                                    });

                                    if (result?.data?.addIncidentReport) {
                                        setDraft({
                                            ...EMPTY_REPORT,
                                            reportedBy: activeUsername,
                                        });
                                        setShowValidation(false);
                                        setMode("list");
                                    }
                                }}
                            >
                                {saving ? "Submitting..." : "Submit Incident Report"}
                            </Button>
                        </Stack>
                        )}
                        {mode === "create" && showValidation && missingCreateFields.length > 0 && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                Complete all required fields before submitting.
                            </Typography>
                        )}

                        {mode === "view" && hasPremiumComplianceAccess && canReview && activeReport?.status === "Submitted" && (
                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                                startIcon={<CheckCircleIcon />}
                                variant="contained"
                                color="success"
                                disabled={updatingStatus}
                                onClick={() =>
                                    updateStatus(activeReport.id, "Reviewed")
                                }
                            >
                                Mark as Reviewed
                            </Button>
                        </Stack>
                        )}

                        {mode === "view" && hasPremiumComplianceAccess && canReview && activeReport?.status === "Reviewed" && (
                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                                startIcon={<ReplayIcon />}
                                variant="outlined"
                                disabled={updatingStatus}
                                onClick={() =>
                                    updateStatus(activeReport.id, "Submitted")
                                }
                            >
                                Revert to Submitted
                            </Button>
                        </Stack>
                        )}

                        {mode === "view" && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 3 }}
                        >
                            Incident reports are read-only after submission.
                            Status changes are logged and restricted to RN/Admin on Premium.
                        </Typography>
                        )}

                        {mode === "view" && (
                            <Paper
                                variant="outlined"
                                sx={{ mt: 2, p: 2, backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Compliance Workflow
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 1 }}>
                                    <Chip
                                        label={activeReport?.status === "Submitted" || activeReport?.status === "Reviewed" ? "Submitted" : "Draft"}
                                        color={activeReport?.status === "Submitted" || activeReport?.status === "Reviewed" ? "primary" : "default"}
                                        size="small"
                                    />
                                    <Chip
                                        label={activeReport?.status === "Reviewed" ? "Reviewed" : "Awaiting Review"}
                                        color={activeReport?.status === "Reviewed" ? "success" : "warning"}
                                        size="small"
                                    />
                                </Stack>

                                {hasPremiumComplianceAccess ? (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
                                            Audit Trail
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Reported by: {getReporterName(activeReport, activeUsername) || "Unknown"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Submitted at: {formatAuditTimestamp(activeReport?.createdAt)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Last updated at: {formatAuditTimestamp(activeReport?.updatedAt)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Reviewed by: {String(activeReport?.reviewedBy?.user?.username || "").trim() || "Not reviewed"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Reviewed at: {formatAuditTimestamp(activeReport?.reviewedAt)}
                                        </Typography>
                                    </>
                                ) : (
                                    <Box className="no-print" sx={{ mt: 1 }}>
                                        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                                            Premium plan or active trial required for advanced compliance workflows and audit-ready reporting.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setCheckPlanNeeded(true)}
                                        >
                                            Upgrade to Premium
                                        </Button>
                                    </Box>
                                )}
                            </Paper>
                        )}

                        {mode === "view" && (
                            <>
                                <DocumentSignatureSection
                                    title="Incident Report Signatures"
                                    description="Collect signatures from all involved parties before final filing."
                                    roles={incidentSignatureRoles}
                                    signatures={reportSignatures}
                                    onChange={handleIncidentSignatureChange}
                                    disabled={!hasSignatureAccess || checkingPremium}
                                />
                                <Box className="no-print" sx={{ mt: 1 }}>
                                    {checkPlanNeeded && (
                                        <UpgradePlan
                                            featureName="Compliance workflows, audit reporting, and digital signatures"
                                            requiredPlanId={premiumPlanId}
                                            onClose={() => setCheckPlanNeeded(false)}
                                        />
                                    )}
                                    {!checkingPremium && !hasSignatureAccess && (
                                        <>
                                            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                                                Premium plan or active trial required to use digital signatures on Incident Report documents.
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setCheckPlanNeeded(true)}
                                            >
                                                Upgrade to Premium
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

